export const STANDARD_ALLOWANCE = 20000;
export const ADDON_UNIT_SIZE = 5000;
export const ADDON_PRICE_MONTHLY = 10;
export const ADDON_PRICE_ANNUAL = 100;
export const STANDARD_PRICE_MONTHLY = 49;
export const STANDARD_PRICE_ANNUAL = 490;
export const LOW_BALANCE_THRESHOLD = 100;

export function formatCredits(value) {
  if (value == null) return '—';
  return Number(value).toLocaleString();
}

export function formatBillingDollars(value) {
  if (value == null) return '—';
  return `$${Number(value).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

export function formatDollarsFromCents(cents) {
  if (cents == null) return '—';
  return `$${(Number(cents) / 100).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

export function formatDollars(value) {
  if (value == null) return '—';
  return `$${Number(value).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

export function pricePer1kCreditsDisplay(microDollarsPerCredit = 1000) {
  const dollars = (microDollarsPerCredit * 1000) / 1_000_000;
  return `$${dollars.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

export function formatOverageIncrement(chargeIncrementCents = 2500) {
  return formatDollars(chargeIncrementCents / 100);
}

const PLAN_LABELS = {
  FREE: 'Free',
  STANDARD: 'Standard',
  ENTERPRISE: 'Enterprise',
};

export function formatPlanLabel(plan) {
  return PLAN_LABELS[plan] || plan || '—';
}

/** Recurring subscription charge from plan constants (excludes on-demand overage). */
export function formatSubscriptionPrice(subscription) {
  if (!subscription?.plan) return null;
  const plan = subscription.plan;

  if (plan === 'FREE') {
    return { amount: '$0', suffix: null, breakdown: 'No recurring charge' };
  }

  if (plan === 'ENTERPRISE') {
    return {
      amount: 'Custom',
      suffix: null,
      breakdown: 'Invoiced manually — contact sales for pricing',
    };
  }

  if (plan !== 'STANDARD') {
    return null;
  }

  const isAnnual = (subscription.billingInterval || 'MONTHLY') === 'ANNUAL';
  const addonQty = subscription.addonQuantity ?? 0;
  const base = isAnnual ? STANDARD_PRICE_ANNUAL : STANDARD_PRICE_MONTHLY;
  const addonUnit = isAnnual ? ADDON_PRICE_ANNUAL : ADDON_PRICE_MONTHLY;
  const total = base + addonQty * addonUnit;

  return {
    amount: formatBillingDollars(total),
    suffix: isAnnual ? '/year' : '/month',
    breakdown:
      addonQty > 0
        ? `${formatBillingDollars(base)} plan + ${addonQty.toLocaleString()} bundle${addonQty === 1 ? '' : 's'} (${formatBillingDollars(addonUnit)} each)`
        : null,
  };
}

/** Dollar savings when moving Standard subscription from monthly to annual billing. */
export function computeAnnualBillingSavings(subscription) {
  const addonQty = subscription?.addonQuantity ?? 0;
  const monthlyTotal = STANDARD_PRICE_MONTHLY + addonQty * ADDON_PRICE_MONTHLY;
  const annualTotal = STANDARD_PRICE_ANNUAL + addonQty * ADDON_PRICE_ANNUAL;
  const monthlyYearTotal = monthlyTotal * 12;
  const savings = monthlyYearTotal - annualTotal;

  return { monthlyTotal, annualTotal, monthlyYearTotal, savings };
}

export function formatDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return iso;
  }
}

export function formatShortDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return iso;
  }
}

export function daysUntilUtcDate(iso) {
  if (!iso) return null;
  const target = new Date(iso);
  const now = new Date();
  const targetDay = Date.UTC(target.getUTCFullYear(), target.getUTCMonth(), target.getUTCDate());
  const today = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  return Math.max(0, Math.round((targetDay - today) / 86400000));
}

/** e.g. "(26 days remaining)" */
export function formatResetRemainingLabel(iso) {
  const days = daysUntilUtcDate(iso);
  if (days == null) return null;
  if (days === 0) return '(today)';
  if (days === 1) return '(1 day remaining)';
  return `(${days} days remaining)`;
}

export function pendingChangeCopy(change) {
  const effectiveAt = formatDate(change.effectiveAt);
  const payload = change.payload || {};
  switch (change.action) {
    case 'DOWNGRADE_TO_FREE_AT_PERIOD_END':
      return `Your plan changes to Free on ${effectiveAt}.`;
    case 'REDUCE_ADDON_AT_PERIOD_END':
      return `Credit bundles reducing from ${payload.currentQuantity ?? '—'} to ${payload.newQuantity ?? '—'} on ${effectiveAt}.`;
    default:
      return `Scheduled change on ${effectiveAt}.`;
  }
}
